import {getNamedType, getNullableType, isInputObjectType, isListType, isNonNullType} from "graphql";

export class MiddlewaredResolver extends Function {
    constructor(resolver: Function) {
        super();
        this.resolver = resolver;
        this.middlewares = []

        return new Proxy(this, {
            apply: (target, thisArg, args) => target._call(...args)
        })
    }

    add(f) {
        this.middlewares.push(f);
        return this;
    }

    remove(f) {
        const i = this.middlewares.findIndex(m => m === f);
        i >= 0 && delete this.middlewares[i];
        return this;
    }

    _call(...args) {
        let pipeline;
        this.middlewares.forEach(m => {
            pipeline = pipeline ? pipeline.then(m) : m;
        })
        return pipeline.then(this.resolver)(...args);
    }
}


export function middleware(funcs, resolver) {
    funcs = Array.isArray(funcs) ? funcs : [funcs];
    return async (parent, args, ctx, info) => {
        for (let f of funcs) {
            const res = await f(parent, args, ctx, info);
            if (res) {
                [parent, args, ctx, info] = res;
            }
        }
        return await resolver(parent, args, ctx, info);
    }
}

export async function visitArguments(a, info, context, visitor = {}) {
    const args = {...a}
    delete args.select;
    let path = [];
    let result = a.select ? {select: a.select} : {};
    const argDefs = info.parentType.getFields()[info.fieldName].args;
    for (const argName of Object.keys(args)) {
        const argValue = args[argName];
        const argType = getNamedType(argDefs.find(arg => arg.name === argName).type);
        path.push(argName);
        const apply = async (argType, argValue, argName, path) => {
            const cbs = [visitor.enter, visitor[argType.toString()]].filter(cb => !!cb);
            for (let cb of cbs) {
                const process = await cb(argType, argValue, argName, path, context);
                argValue = process === undefined ? argValue : process;
            }
            return argValue;
        };
        const traverse = async (argType, argValue, argName) => {
            argValue = await apply(argType, argValue, argName, path);
            if (isInputObjectType(argType)) {
                // let newObject = {};
                for (const argFieldName of Object.keys(argValue || {})) {
                    path.push(argFieldName);
                    // console.log(argFieldName);
                    let field = argType.getFields()[argFieldName];
                    // if a previous input handler added a field to input, `field` will be undefined and we skip it
                    if (!field) {
                        return argValue;
                    }
                    let argFieldType = field.type;
                    if (isNonNullType(argFieldType)) {
                        argFieldType = getNullableType(argFieldType);
                    }
                    if (isListType(argFieldType) || isListType(argFieldType.ofType)) {
                        const innerType = getNamedType(argFieldType);
                        argValue[argFieldName] =  await Promise.all(argValue[argFieldName].map(async (v, i) => {
                            path.push(i);
                            const tr =  await traverse(innerType, v, argFieldName);
                            path.pop();
                            return tr;
                        }));
                    }
                    else if (isInputObjectType(argFieldType)) {
                        argValue[argFieldName] =  await traverse(argFieldType, argValue[argFieldName], argFieldName)
                    } else {
                        argValue[argFieldName] = await apply(argFieldType, argValue[argFieldName], argFieldName, path);
                    }
                    path.pop();
                    // newObject[argFieldName] = argValue;
                }
                // return newObject;
            }
            return argValue;
        };

        const processed = await traverse(argType, argValue, argName);
        switch (processed) {
            case undefined:
                result[argName] = argValue;
                break;
            case null:
                break;
            default:
                result[argName] = processed;
        }
        path = [];
    }
    return result;
}
